import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Filter, Car, Eye, Pencil, Trash2, DollarSign, ClipboardList,
  ClipboardCheck, LogIn, FileSignature, FileText, TrendingUp, Calculator,
  Paperclip, Megaphone, Bookmark, Wrench, CheckCircle2,
} from 'lucide-react';
import { api } from '../../services/api';
import { PageHeader, DataTable, Column, SortConfig, SearchInput, ActionMenu, ActionMenuItem, ConfirmDialog } from '../../components/ui';
import { Card, buttonClass } from '../../design-system';
import { formatCurrency, formatKm, formatPlaca } from '../../utils/format';
import { useApiEffect, useDebounce } from '../../hooks';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { PAGE_SIZE, veiculoStatusLabels as statusLabels, veiculoStatusColors as statusColors } from '../../constants';
import {
  CustosVeiculoModal, PendenciasVeiculoModal, VistoriasVeiculoModal,
  DespachanteVeiculoModal, EntradaVeiculoModal, NfeVeiculoModal,
  FipeVeiculoModal, FinanceiroVeiculoModal, ArquivosVeiculoModal,
  PublicacoesVeiculoModal,
} from '../../components/veiculos';

interface VeiculoList {
  id: number;
  marcaNome: string;
  modeloNome: string;
  versao: string | null;
  anoFabricacao: number;
  anoModelo: number;
  cor: string | null;
  placa: string | null;
  km: number | null;
  valorVenda: number;
  valorCompra: number | null;
  status: string;
  fotoPrincipal: string | null;
  diasEstoque: number;
  dataCadastro: string;
}

interface PagedResult {
  items: VeiculoList[];
  totalItems: number;
  page: number;
  pageSize: number;
}

type VeiculoModalType =
  | 'custos' | 'pendencias' | 'vistorias' | 'despachante' | 'entrada'
  | 'nfe' | 'fipe' | 'financeiro' | 'arquivos' | 'publicacoes';

export function VeiculosList() {
  const navigate = useNavigate();
  const { showSuccess, showApiError } = useNotification();
  const { isAdmin, isGerente } = useAuth();
  const canManage = isAdmin || isGerente;

  const [data, setData] = useState<PagedResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState('DISPONIVEL');
  const [showFilters, setShowFilters] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'dataCadastro', direction: 'desc' });

  // Ação em modal: um único host de modais para a lista, parametrizado pelo veículo selecionado
  const [activeModal, setActiveModal] = useState<{ type: VeiculoModalType; veiculo: VeiculoList } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<VeiculoList | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Debounce da busca para evitar requisições excessivas durante digitação
  const buscaDebounced = useDebounce(busca, 400);

  const fetchData = useCallback(async (signal: AbortSignal) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: PAGE_SIZE.toString(),
      });
      if (buscaDebounced) params.append('busca', buscaDebounced);
      if (status) params.append('status', status);
      if (sortConfig.direction) {
        params.append('orderBy', sortConfig.key);
        params.append('orderDirection', sortConfig.direction);
      }

      const response = await api.get<PagedResult>(`/veiculos?${params}`, { signal });
      setData(response.data);
    } finally {
      setIsLoading(false);
    }
  }, [page, buscaDebounced, status, sortConfig]);

  const { refetch } = useApiEffect(fetchData, [page, buscaDebounced, status, sortConfig]);

  const handleSort = (config: SortConfig) => {
    setSortConfig(config);
    setPage(1);
  };

  const handleStatusChange = async (item: VeiculoList, novoStatus: string) => {
    try {
      await api.put(`/veiculos/${item.id}/status`, { status: novoStatus });
      showSuccess(`Situação alterada para ${statusLabels[novoStatus] || novoStatus}`);
      refetch();
    } catch (error) {
      showApiError(error);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await api.delete(`/veiculos/${deleteTarget.id}`);
      showSuccess('Veículo excluído com sucesso');
      setDeleteTarget(null);
      refetch();
    } catch (error) {
      showApiError(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const buildActions = (item: VeiculoList): ActionMenuItem[] => {
    const isVendido = item.status === 'VENDIDO';

    // Ações de situação (não disponíveis para veículos vendidos — venda passa pelo fluxo de Vendas)
    const statusActions: ActionMenuItem[] = [];
    if (canManage && !isVendido) {
      if (item.status !== 'DISPONIVEL') {
        statusActions.push({ key: 'st-disp', label: 'Marcar como Disponível', icon: <CheckCircle2 className="w-4 h-4" />, onSelect: () => handleStatusChange(item, 'DISPONIVEL') });
      }
      if (item.status !== 'RESERVADO') {
        statusActions.push({ key: 'st-res', label: 'Reservar', icon: <Bookmark className="w-4 h-4" />, onSelect: () => handleStatusChange(item, 'RESERVADO') });
      }
      if (item.status !== 'EM_PREPARACAO') {
        statusActions.push({ key: 'st-prep', label: 'Em Preparação', icon: <Wrench className="w-4 h-4" />, onSelect: () => handleStatusChange(item, 'EM_PREPARACAO') });
      }
      if (statusActions.length > 0) statusActions[0].dividerBefore = true;
    }

    const openModal = (type: VeiculoModalType) => () => setActiveModal({ type, veiculo: item });

    return [
      { key: 'ver', label: 'Ver detalhes', icon: <Eye className="w-4 h-4" />, onSelect: () => navigate(`/veiculos/${item.id}`) },
      { key: 'editar', label: 'Editar', icon: <Pencil className="w-4 h-4" />, onSelect: () => navigate(`/veiculos/${item.id}/editar`) },
      ...statusActions,
      { key: 'custos', label: 'Custos', icon: <DollarSign className="w-4 h-4" />, onSelect: openModal('custos'), dividerBefore: true },
      { key: 'pendencias', label: 'Pendências', icon: <ClipboardList className="w-4 h-4" />, onSelect: openModal('pendencias') },
      { key: 'vistorias', label: 'Vistorias', icon: <ClipboardCheck className="w-4 h-4" />, onSelect: openModal('vistorias') },
      { key: 'entrada', label: 'Entrada de estoque', icon: <LogIn className="w-4 h-4" />, onSelect: openModal('entrada') },
      { key: 'despachante', label: 'Despachante', icon: <FileSignature className="w-4 h-4" />, onSelect: openModal('despachante') },
      { key: 'nfe', label: 'Nota Fiscal', icon: <FileText className="w-4 h-4" />, onSelect: openModal('nfe') },
      { key: 'fipe', label: 'FIPE', icon: <TrendingUp className="w-4 h-4" />, onSelect: openModal('fipe') },
      { key: 'financeiro', label: 'Financeiro', icon: <Calculator className="w-4 h-4" />, onSelect: openModal('financeiro') },
      { key: 'arquivos', label: 'Arquivos', icon: <Paperclip className="w-4 h-4" />, onSelect: openModal('arquivos') },
      { key: 'publicacoes', label: 'Anúncios / Publicações', icon: <Megaphone className="w-4 h-4" />, onSelect: openModal('publicacoes') },
      { key: 'excluir', label: 'Excluir', icon: <Trash2 className="w-4 h-4" />, variant: 'danger', hidden: !canManage, dividerBefore: true, onSelect: () => setDeleteTarget(item) },
    ];
  };

  const columns: Column<VeiculoList>[] = [
    {
      key: 'foto',
      header: '',
      width: '60px',
      render: (item) => (
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center">
          {item.fotoPrincipal ? (
            <img src={item.fotoPrincipal} alt="" className="w-full h-full object-cover" />
          ) : (
            <Car className="w-6 h-6 text-slate-400" />
          )}
        </div>
      ),
    },
    {
      key: 'marcaNome',
      header: 'Veículo',
      sortable: true,
      render: (item) => (
        <div>
          <div className="font-medium text-slate-900">
            {item.marcaNome} {item.modeloNome}
          </div>
          <div className="text-sm text-slate-500">
            {item.versao && `${item.versao} • `}
            {item.anoFabricacao}/{item.anoModelo}
          </div>
        </div>
      ),
    },
    {
      key: 'placa',
      header: 'Placa',
      sortable: true,
      render: (item) => (
        <span className="font-mono">{formatPlaca(item.placa)}</span>
      ),
    },
    {
      key: 'cor',
      header: 'Cor',
      sortable: true,
      render: (item) => item.cor || '-',
    },
    {
      key: 'km',
      header: 'KM',
      align: 'right',
      sortable: true,
      render: (item) => formatKm(item.km),
    },
    {
      key: 'valorVenda',
      header: 'Valor',
      align: 'right',
      sortable: true,
      render: (item) => (
        <span className="font-bold text-slate-900 tabular-nums">
          {formatCurrency(item.valorVenda)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (item) => (
        <span
          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusColors[item.status] || 'bg-slate-100 text-slate-600'}`}
        >
          {statusLabels[item.status] || item.status}
        </span>
      ),
    },
    {
      key: 'diasEstoque',
      header: 'Dias',
      align: 'center',
      sortable: true,
      render: (item) => (
        <span className={`tabular-nums ${item.diasEstoque > 60 ? 'text-rose-600 font-semibold' : 'text-slate-600'}`}>
          {item.diasEstoque}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '56px',
      align: 'center',
      render: (item) => <ActionMenu ariaLabel="Ações" items={buildActions(item)} />,
    },
  ];

  const veiculoNome = activeModal ? `${activeModal.veiculo.marcaNome} ${activeModal.veiculo.modeloNome}` : '';
  const veiculoId = activeModal?.veiculo.id ?? 0;
  const closeModal = () => setActiveModal(null);

  return (
    <div>
      <PageHeader title="Veículos" subtitle="Gerencie o estoque de veículos">
        <button
          onClick={() => navigate('/veiculos/novo')}
          className={buttonClass('primary')}
        >
          <Plus className="w-4 h-4" />
          Novo Veículo
        </button>
      </PageHeader>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <SearchInput
            value={busca}
            onChange={setBusca}
            placeholder="Buscar por marca, modelo ou placa..."
          />
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="h-10 rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          >
            <option value="">Todos os status</option>
            <option value="DISPONIVEL">Disponível</option>
            <option value="RESERVADO">Reservado</option>
            <option value="VENDIDO">Vendido</option>
            <option value="EM_PREPARACAO">Em Preparação</option>
          </select>

          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={buttonClass('secondary')}
          >
            <Filter className="w-4 h-4" />
            Filtros
          </button>
        </div>
      </Card>

      {/* Table */}
      <DataTable
        columns={columns}
        data={data?.items || []}
        keyField="id"
        isLoading={isLoading}
        emptyMessage="Nenhum veículo encontrado"
        page={page}
        pageSize={PAGE_SIZE}
        totalItems={data?.totalItems}
        onPageChange={setPage}
        sortConfig={sortConfig}
        onSort={handleSort}
        onRowClick={(item) => navigate(`/veiculos/${item.id}`)}
      />

      {/* Confirmação de exclusão */}
      <ConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        type="danger"
        title="Excluir Veículo"
        message={
          deleteTarget
            ? `Tem certeza que deseja excluir o veículo ${deleteTarget.marcaNome} ${deleteTarget.modeloNome}? Esta ação não pode ser desfeita.`
            : ''
        }
        confirmText="Excluir"
        isLoading={isDeleting}
      />

      {/* Host único dos modais de gestão — abertos pelo menu de Ações.
          A key por veículo força remontagem (estado interno limpo) ao trocar de veículo. */}
      <CustosVeiculoModal key={`custos-${veiculoId}`} isOpen={activeModal?.type === 'custos'} onClose={closeModal} veiculoId={veiculoId} veiculoNome={veiculoNome} />
      <PendenciasVeiculoModal key={`pendencias-${veiculoId}`} isOpen={activeModal?.type === 'pendencias'} onClose={closeModal} veiculoId={veiculoId} veiculoNome={veiculoNome} />
      <VistoriasVeiculoModal key={`vistorias-${veiculoId}`} isOpen={activeModal?.type === 'vistorias'} onClose={closeModal} veiculoId={veiculoId} veiculoNome={veiculoNome} />
      <DespachanteVeiculoModal key={`despachante-${veiculoId}`} isOpen={activeModal?.type === 'despachante'} onClose={closeModal} veiculoId={veiculoId} veiculoNome={veiculoNome} />
      <EntradaVeiculoModal key={`entrada-${veiculoId}`} isOpen={activeModal?.type === 'entrada'} onClose={closeModal} veiculoId={veiculoId} veiculoNome={veiculoNome} />
      <NfeVeiculoModal key={`nfe-${veiculoId}`} isOpen={activeModal?.type === 'nfe'} onClose={closeModal} veiculoId={veiculoId} veiculoNome={veiculoNome} />
      <FipeVeiculoModal key={`fipe-${veiculoId}`} isOpen={activeModal?.type === 'fipe'} onClose={closeModal} veiculoId={veiculoId} veiculoNome={veiculoNome} />
      <FinanceiroVeiculoModal key={`financeiro-${veiculoId}`} isOpen={activeModal?.type === 'financeiro'} onClose={closeModal} veiculoId={veiculoId} veiculoNome={veiculoNome} />
      <ArquivosVeiculoModal key={`arquivos-${veiculoId}`} isOpen={activeModal?.type === 'arquivos'} onClose={closeModal} veiculoId={veiculoId} veiculoNome={veiculoNome} />
      <PublicacoesVeiculoModal key={`publicacoes-${veiculoId}`} isOpen={activeModal?.type === 'publicacoes'} onClose={closeModal} veiculoId={veiculoId} veiculoNome={veiculoNome} />
    </div>
  );
}
